import { useState, useEffect } from 'react';
import { Settings, GripVertical, Plus, Trash2, Eye, EyeOff, Loader2, Save, Upload, Volume2, Type, Palette, ChevronDown, ChevronRight, Hash } from 'lucide-react';
import { EffectType, SoundType, BrollSourceType, ComponentType, EFFECTS, SOUNDS, BROLL_SOURCES, COMPONENT_TYPES } from '../types';

const S3_WORKER = 'https://scene-editor-s3.daniel-807.workers.dev';
const SETTINGS_KEY = 'settings/global.json';

// Safe array helper - ensures we always get an array even if input is object/undefined/null
const ensureArray = <T,>(value: T[] | unknown): T[] => Array.isArray(value) ? value : [];

// Google Fonts configuration
const GOOGLE_FONTS = [
  { name: 'Poppins', weights: [400, 500, 600, 700] },
  { name: 'Inter', weights: [400, 500, 600, 700] },
  { name: 'Montserrat', weights: [400, 500, 600, 700] },
  { name: 'Roboto', weights: [400, 500, 700] },
  { name: 'Playfair Display', weights: [400, 500, 600, 700] },
  { name: 'Space Grotesk', weights: [400, 500, 600, 700] },
  { name: 'DM Sans', weights: [400, 500, 600, 700] },
  { name: 'Outfit', weights: [400, 500, 600, 700] },
  { name: 'Sora', weights: [400, 500, 600, 700] },
  { name: 'Manrope', weights: [400, 500, 600, 700, 800] },
];

const FONT_WEIGHTS = [
  { value: 400, label: 'Regular' },
  { value: 500, label: 'Medium' },
  { value: 600, label: 'Semibold' },
  { value: 700, label: 'Bold' },
];

const PRESET_COLORS = [
  { name: 'Antelope Teal', value: '#34A49F' },
  { name: 'Antelope Dark', value: '#0A1629' },
  { name: 'Deep Navy', value: '#1A2744' },
  { name: 'Slate', value: '#374151' },
  { name: 'Charcoal', value: '#1F2937' },
  { name: 'Midnight', value: '#0F172A' },
  { name: 'Teal Dark', value: '#134E4A' },
  { name: 'Purple Dark', value: '#3B0764' },
  { name: 'Transparent', value: 'transparent' },
];

const EDGE_STYLES = [
  { id: 'pill', name: 'Pill', radius: 9999, description: 'Fully rounded ends' },
  { id: 'rounded', name: 'Rounded', radius: 12, description: 'Soft corners' },
  { id: 'square', name: 'Square', radius: 0, description: 'Sharp corners' },
  { id: 'circle', name: 'Circle', radius: 9999, description: 'For single words' },
];

export interface CaptionSettings {
  fontFamily: string;
  fontWeight: number;
  fontSize: number;
  backgroundColor: string;
  textColor: string;
  highlightColor: string; // Active word color (TikTok-style)
  padding: number;
  borderRadius: number;
  edgeStyle: 'pill' | 'rounded' | 'square' | 'circle';
  wordsShown: number;
  textCase: 'uppercase' | 'capitalize' | 'lowercase' | 'none';
  position: number; // 1-9, where 1=top, 9=bottom
  widthMode: 'snap' | 'fixed'; // snap = instant width change, fixed = constant width
}

export interface CaptionPreset {
  id: string;
  name: string;
  settings: CaptionSettings;
}

export interface AppSettings {
  // Animations - ordered list with visibility
  animations: { id: EffectType; enabled: boolean }[];
  // Sounds - with URLs and visibility
  sounds: { id: string; name: string; url: string; enabled: boolean }[];
  // B-roll sources - toggle
  brollSources: { id: BrollSourceType; enabled: boolean }[];
  // Component types
  components: { id: string; name: string; icon: string; enabled: boolean }[];
  // Caption typography settings
  captions: CaptionSettings;
  // Saved caption presets
  captionPresets: CaptionPreset[];
  // Timestamps
  updatedAt?: string;
}

const DEFAULT_CAPTIONS: CaptionSettings = {
  fontFamily: 'Poppins',
  fontWeight: 600,
  fontSize: 32,
  backgroundColor: '#34A49F',
  textColor: '#FFFFFF',
  highlightColor: '#FFCC00', // Yellow highlight for active word
  padding: 16,
  borderRadius: 9999,
  edgeStyle: 'pill',
  wordsShown: 2,
  textCase: 'capitalize',
  position: 8, // Lower (near bottom)
  widthMode: 'snap', // Default: instant width changes
};

const POSITION_LABELS = [
  { value: 1, label: 'Top Edge' },
  { value: 2, label: 'Upper' },
  { value: 3, label: 'Upper Mid' },
  { value: 4, label: 'Mid Upper' },
  { value: 5, label: 'Center' },
  { value: 6, label: 'Mid Lower' },
  { value: 7, label: 'Lower Mid' },
  { value: 8, label: 'Lower' },
  { value: 9, label: 'Bottom Edge' },
];

const TEXT_CASES = [
  { id: 'uppercase', name: 'CAPS', description: 'ALL UPPERCASE' },
  { id: 'capitalize', name: 'Proper', description: 'Title Case' },
  { id: 'lowercase', name: 'lower', description: 'all lowercase' },
  { id: 'none', name: 'None', description: 'As typed' },
];

const DEFAULT_SETTINGS: AppSettings = {
  animations: EFFECTS.map(e => ({ id: e.id, enabled: true })),
  sounds: SOUNDS.map(s => ({ id: s.id, name: s.name, url: s.url, enabled: true })),
  brollSources: BROLL_SOURCES.map(b => ({ id: b.id, enabled: true })),
  components: COMPONENT_TYPES.map(c => ({ id: c.id, name: c.name, icon: c.icon, enabled: true })),
  captions: DEFAULT_CAPTIONS,
  captionPresets: [],
};

// Load Google Fonts dynamically
function loadGoogleFont(fontFamily: string, weights: number[]) {
  const existingLink = document.querySelector(`link[data-font="${fontFamily}"]`);
  if (existingLink) return;
  
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(' ', '+')}:wght@${weights.join(';')}&display=swap`;
  link.setAttribute('data-font', fontFamily);
  document.head.appendChild(link);
}

async function loadSettings(): Promise<AppSettings> {
  try {
    const response = await fetch(`${S3_WORKER}/get-settings`);
    if (response.ok) {
      const data = await response.json();
      if (data.settings) {
        // Merge with defaults to ensure all arrays exist
        return {
          ...DEFAULT_SETTINGS,
          ...data.settings,
          // Ensure arrays exist (saved settings might only have captions)
          animations: data.settings.animations || DEFAULT_SETTINGS.animations,
          sounds: data.settings.sounds || DEFAULT_SETTINGS.sounds,
          brollSources: data.settings.brollSources || DEFAULT_SETTINGS.brollSources,
          components: data.settings.components || DEFAULT_SETTINGS.components,
          captionPresets: data.settings.captionPresets || [],
        };
      }
    }
  } catch (err) {
    console.error('Failed to load settings:', err);
  }
  return DEFAULT_SETTINGS;
}

async function saveSettings(settings: AppSettings): Promise<boolean> {
  try {
    const response = await fetch(`${S3_WORKER}/save-settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ settings: { ...settings, updatedAt: new Date().toISOString() } }),
    });
    const data = await response.json();
    return data.success;
  } catch (err) {
    console.error('Failed to save settings:', err);
    return false;
  }
}

export function SettingsPanel() {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeSection, setActiveSection] = useState<'typography' | 'animations' | 'sounds' | 'sources' | 'components'>('typography');
  const [newSoundName, setNewSoundName] = useState('');
  const [newSoundUrl, setNewSoundUrl] = useState('');
  const [newComponentName, setNewComponentName] = useState('');
  const [newComponentIcon, setNewComponentIcon] = useState('🧩');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(new Set(['font']));
  const [fontDropdownOpen, setFontDropdownOpen] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [playingAnimation, setPlayingAnimation] = useState<string | null>(null);

  // Animation preview component
  const AnimationPreview = ({ effectId }: { effectId: string }) => {
    const isPlaying = playingAnimation === effectId;
    
    const getAnimationStyle = (): React.CSSProperties => {
      if (!isPlaying) return {};
      
      const animations: Record<string, string> = {
        'slide-up': 'slideUp 0.5s ease-out forwards',
        'slide-down': 'slideDown 0.5s ease-out forwards',
        'slide-left': 'slideLeft 0.5s ease-out forwards',
        'slide-right': 'slideRight 0.5s ease-out forwards',
        'scale-pop': 'scalePop 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
        'scale-shrink': 'scaleShrink 0.4s ease-out forwards',
        'bounce-drop': 'bounceDrop 0.6s ease-out forwards',
        'bounce-up': 'bounceUp 0.6s ease-out forwards',
        'fade': 'fadeIn 0.5s ease-out forwards',
        'elastic-resize': 'elasticResize 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards',
        'zoom-burst': 'zoomBurst 0.3s ease-out forwards',
        'zoom-pulse': 'zoomPulse 0.5s ease-in-out forwards',
        'rotate-in': 'rotateIn 0.5s ease-out forwards',
        'rotate-out': 'rotateOut 0.5s ease-out forwards',
        'swing-in': 'swingIn 0.5s ease-out forwards',
        'swing-out': 'swingOut 0.5s ease-out forwards',
        'diagonal-slide': 'diagonalSlide 0.5s ease-out forwards',
        'wipe-left': 'wipeLeft 0.4s ease-out forwards',
        'wipe-down': 'wipeDown 0.4s ease-out forwards',
        'flip-in': 'flipIn 0.5s ease-out forwards',
        'squeeze': 'squeeze 0.4s ease-out forwards',
        'stretch': 'stretch 0.4s ease-out forwards',
        'shake': 'shake 0.5s ease-out forwards',
      };
      
      return { animation: animations[effectId] || 'none' };
    };
    
    const playAnimation = () => {
      setPlayingAnimation(null);
      setTimeout(() => setPlayingAnimation(effectId), 10);
      setTimeout(() => setPlayingAnimation(null), 1000);
    };
    
    if (effectId === 'none') {
      return <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-400">—</div>;
    }
    
    return (
      <button
        onClick={playAnimation}
        className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center hover:bg-gray-200 transition-colors"
        title="Click to preview"
      >
        <div 
          className="w-4 h-4 bg-green-500 rounded-sm"
          style={getAnimationStyle()}
        />
      </button>
    );
  };

  const toggleAccordion = (id: string) => {
    setExpandedAccordions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    loadSettings().then(s => {
      // Deep merge with ALL defaults to ensure nothing is undefined
      const mergedSettings: AppSettings = {
        animations: s?.animations || DEFAULT_SETTINGS.animations,
        sounds: s?.sounds || DEFAULT_SETTINGS.sounds,
        brollSources: s?.brollSources || DEFAULT_SETTINGS.brollSources,
        components: s?.components || DEFAULT_SETTINGS.components,
        captions: { ...DEFAULT_CAPTIONS, ...(s?.captions || {}) },
        captionPresets: s?.captionPresets || [],
        updatedAt: s?.updatedAt,
      };
      setSettings(mergedSettings);
      setLoading(false);
    }).catch(err => {
      console.error('Failed to load settings:', err);
      setSettings(DEFAULT_SETTINGS);
      setLoading(false);
    });
  }, []);

  // Load all Google Fonts on mount
  useEffect(() => {
    GOOGLE_FONTS.forEach(font => loadGoogleFont(font.name, font.weights));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const success = await saveSettings(settings);
    setSaving(false);
    setHasChanges(false);
    if (!success) alert('Failed to save settings');
  };

  const updateSettings = (newSettings: AppSettings) => {
    setSettings(newSettings);
    setHasChanges(true);
  };

  // Animation handlers
  const toggleAnimation = (id: EffectType) => {
    updateSettings({
      ...settings,
      animations: ensureArray(settings.animations).map(a => a.id === id ? { ...a, enabled: !a.enabled } : a),
    });
  };

  const moveAnimation = (fromIndex: number, toIndex: number) => {
    const newAnimations = [...settings.animations];
    const [moved] = newAnimations.splice(fromIndex, 1);
    newAnimations.splice(toIndex, 0, moved);
    updateSettings({ ...settings, animations: newAnimations });
  };

  // Sound handlers
  const toggleSound = (id: string) => {
    updateSettings({
      ...settings,
      sounds: ensureArray(settings.sounds).map(s => s.id === id ? { ...s, enabled: !s.enabled } : s),
    });
  };

  const addSound = () => {
    if (!newSoundName || !newSoundUrl) return;
    const id = newSoundName.toLowerCase().replace(/\s+/g, '-');
    updateSettings({
      ...settings,
      sounds: [...settings.sounds, { id, name: newSoundName, url: newSoundUrl, enabled: true }],
    });
    setNewSoundName('');
    setNewSoundUrl('');
  };

  const removeSound = (id: string) => {
    updateSettings({
      ...settings,
      sounds: settings.sounds.filter(s => s.id !== id),
    });
  };

  // B-roll source handlers
  const toggleSource = (id: BrollSourceType) => {
    updateSettings({
      ...settings,
      brollSources: ensureArray(settings.brollSources).map(b => b.id === id ? { ...b, enabled: !b.enabled } : b),
    });
  };

  // Component handlers
  const toggleComponent = (id: string) => {
    updateSettings({
      ...settings,
      components: ensureArray(settings.components).map(c => c.id === id ? { ...c, enabled: !c.enabled } : c),
    });
  };

  // Caption handlers
  const updateCaptions = (updates: Partial<CaptionSettings>) => {
    updateSettings({
      ...settings,
      captions: { ...settings.captions, ...updates },
    });
  };

  // Preset handlers
  const savePreset = () => {
    if (!newPresetName.trim()) return;
    const id = newPresetName.toLowerCase().replace(/\s+/g, '-') + '-' + Date.now();
    const newPreset: CaptionPreset = {
      id,
      name: newPresetName.trim(),
      settings: { ...settings.captions },
    };
    updateSettings({
      ...settings,
      captionPresets: [...ensureArray(settings.captionPresets), newPreset],
    });
    setNewPresetName('');
  };

  const loadPreset = (preset: CaptionPreset) => {
    updateSettings({
      ...settings,
      captions: { ...preset.settings },
    });
  };

  const deletePreset = (presetId: string) => {
    updateSettings({
      ...settings,
      captionPresets: ensureArray(settings.captionPresets).filter(p => p.id !== presetId),
    });
  };

  const addComponent = () => {
    if (!newComponentName) return;
    const id = newComponentName.toLowerCase().replace(/\s+/g, '-');
    updateSettings({
      ...settings,
      components: [...settings.components, { id, name: newComponentName, icon: newComponentIcon, enabled: true }],
    });
    setNewComponentName('');
    setNewComponentIcon('🧩');
  };

  const removeComponent = (id: string) => {
    updateSettings({
      ...settings,
      components: settings.components.filter(c => c.id !== id),
    });
  };

  // Play sound preview
  const playSound = (url: string) => {
    if (url) {
      const audio = new Audio(url);
      audio.volume = 0.7;
      audio.play().catch(() => {});
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-3 py-4 sm:px-4 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Settings className="w-6 h-6 text-gray-600" />
          <h1 className="text-xl font-semibold text-gray-900">Settings</h1>
        </div>
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            hasChanges 
              ? 'bg-green-600 text-white hover:bg-green-700' 
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-0.5 sm:gap-1 mb-4 bg-gray-100 p-0.5 sm:p-1 rounded-lg overflow-x-auto">
        {[
          { id: 'typography', label: 'Type' },
          { id: 'animations', label: 'Anim' },
          { id: 'sounds', label: 'Sound' },
          { id: 'sources', label: 'Source' },
          { id: 'components', label: 'Comp' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id as any)}
            className={`flex-1 py-1.5 sm:py-2 px-1.5 sm:px-3 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
              activeSection === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Typography Section */}
      {activeSection === 'typography' && (
        <div className="space-y-2">
          {/* Live Preview Card - Shows base vs highlight contrast */}
          <div 
            className="text-center flex items-center justify-center gap-2"
            style={{ 
              backgroundColor: settings.captions?.backgroundColor || '#34A49F',
              borderRadius: EDGE_STYLES.find(e => e.id === settings.captions?.edgeStyle)?.radius || 9999,
              padding: `${settings.captions?.padding || 16}px`,
            }}
          >
            {/* Base word */}
            <span 
              style={{ 
                fontFamily: `'${settings.captions?.fontFamily || 'Poppins'}', sans-serif`,
                fontWeight: settings.captions?.fontWeight || 600,
                color: settings.captions?.textColor || '#FFFFFF',
                fontSize: `${(settings.captions?.fontSize || 32) * 0.8}px`,
                textTransform: (settings.captions?.textCase || 'capitalize') === 'none' ? 'none' : settings.captions?.textCase || 'capitalize',
              }}
            >
              the
            </span>
            {/* Highlighted word */}
            <span 
              style={{ 
                fontFamily: `'${settings.captions?.fontFamily || 'Poppins'}', sans-serif`,
                fontWeight: settings.captions?.fontWeight || 600,
                color: settings.captions?.highlightColor || '#FFCC00',
                fontSize: `${(settings.captions?.fontSize || 32) * 0.8}px`,
                textTransform: (settings.captions?.textCase || 'capitalize') === 'none' ? 'none' : settings.captions?.textCase || 'capitalize',
              }}
            >
              MARKET
            </span>
          </div>
          
          {/* Current Settings Summary */}
          <div className="mb-4 py-2 text-xs text-gray-500 text-center">
            {settings.captions?.fontFamily || 'Poppins'} · {FONT_WEIGHTS.find(w => w.value === settings.captions?.fontWeight)?.label || 'Semibold'} · {settings.captions?.wordsShown || 2} words · {TEXT_CASES.find(c => c.id === settings.captions?.textCase)?.name || 'Proper'} · {EDGE_STYLES.find(e => e.id === settings.captions?.edgeStyle)?.name || 'Pill'} · Pos {settings.captions?.position || 7}
          </div>

          {/* Saved Presets */}
          {(settings.captionPresets?.length > 0) && (
            <div className="bg-white rounded-lg border border-gray-200 p-3 mb-2">
              <div className="text-xs font-medium text-gray-500 mb-2">Saved Presets</div>
              <div className="flex flex-wrap gap-2">
                {ensureArray(settings.captionPresets).map(preset => (
                  <div key={preset.id} className="flex items-center gap-1 bg-gray-100 rounded-full pl-3 pr-1 py-1">
                    <button
                      onClick={() => loadPreset(preset)}
                      className="text-sm text-gray-700 hover:text-green-600"
                    >
                      {preset.name}
                    </button>
                    <button
                      onClick={() => deletePreset(preset.id)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Save as Preset */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newPresetName}
              onChange={(e) => setNewPresetName(e.target.value)}
              placeholder="Save current as preset..."
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              onKeyDown={(e) => e.key === 'Enter' && savePreset()}
            />
            <button
              onClick={savePreset}
              disabled={!newPresetName.trim()}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm disabled:opacity-50 hover:bg-green-700"
            >
              Save
            </button>
          </div>

          {/* Font Family Accordion */}
          <div className={`bg-white rounded-lg border border-gray-200 ${expandedAccordions.has('font') ? 'overflow-visible z-40 relative' : 'overflow-hidden'}`}>
            <button
              onClick={() => toggleAccordion('font')}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                {expandedAccordions.has('font') ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <Type className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Font Family</span>
              </div>
              <span 
                className="text-sm text-gray-500"
                style={{ fontFamily: `'${settings.captions?.fontFamily || 'Poppins'}', sans-serif` }}
              >
                {settings.captions?.fontFamily || 'Poppins'}
              </span>
            </button>
            {expandedAccordions.has('font') && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="relative mt-3">
                  <button
                    onClick={() => setFontDropdownOpen(!fontDropdownOpen)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-left flex items-center justify-between hover:border-gray-300"
                  >
                    <span style={{ fontFamily: `'${settings.captions?.fontFamily || 'Poppins'}', sans-serif`, fontWeight: 500 }}>
                      {settings.captions?.fontFamily || 'Poppins'} — Aa Bb Cc 123
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>
                  {fontDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {GOOGLE_FONTS.map(font => (
                        <button
                          key={font.name}
                          onClick={() => { updateCaptions({ fontFamily: font.name }); setFontDropdownOpen(false); }}
                          className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between ${
                            settings.captions?.fontFamily === font.name ? 'bg-green-50' : ''
                          }`}
                        >
                          <span style={{ fontFamily: `'${font.name}', sans-serif`, fontWeight: 500 }}>
                            {font.name}
                          </span>
                          <span className="text-xs text-gray-400" style={{ fontFamily: `'${font.name}', sans-serif` }}>
                            Aa Bb Cc 123
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Font Weight Accordion */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggleAccordion('weight')}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                {expandedAccordions.has('weight') ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <span className="text-sm font-medium text-gray-700">Font Weight</span>
              </div>
              <span className="text-sm text-gray-500">{FONT_WEIGHTS.find(w => w.value === settings.captions?.fontWeight)?.label || 'Semibold'}</span>
            </button>
            {expandedAccordions.has('weight') && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="flex gap-2 mt-3">
                  {FONT_WEIGHTS.map(weight => (
                    <button
                      key={weight.value}
                      onClick={() => updateCaptions({ fontWeight: weight.value })}
                      className={`flex-1 py-2 px-2 rounded-lg border-2 transition-all text-xs ${
                        settings.captions?.fontWeight === weight.value
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ fontWeight: weight.value }}
                    >
                      {weight.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Words Shown Accordion */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggleAccordion('words')}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                {expandedAccordions.has('words') ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <Hash className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Words Shown</span>
              </div>
              <span className="text-sm text-gray-500">{settings.captions?.wordsShown || 2} words</span>
            </button>
            {expandedAccordions.has('words') && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="flex gap-2 mt-3">
                  {[1, 2, 3, 4, 5, 6].map(num => (
                    <button
                      key={num}
                      onClick={() => updateCaptions({ wordsShown: num })}
                      className={`flex-1 py-2 rounded-lg border-2 transition-all text-sm font-medium ${
                        settings.captions?.wordsShown === num
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Text Case Accordion */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggleAccordion('case')}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                {expandedAccordions.has('case') ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <span className="text-sm font-medium text-gray-700">Text Case</span>
              </div>
              <span className="text-sm text-gray-500">{TEXT_CASES.find(c => c.id === settings.captions?.textCase)?.name || 'Proper'}</span>
            </button>
            {expandedAccordions.has('case') && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {TEXT_CASES.map(tc => (
                    <button
                      key={tc.id}
                      onClick={() => updateCaptions({ textCase: tc.id as any })}
                      className={`py-2 rounded-lg border-2 transition-all text-center ${
                        settings.captions?.textCase === tc.id
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-sm font-medium">{tc.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Edge Style Accordion */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggleAccordion('edge')}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                {expandedAccordions.has('edge') ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <span className="text-sm font-medium text-gray-700">Edge Style</span>
              </div>
              <span className="text-sm text-gray-500">{EDGE_STYLES.find(e => e.id === settings.captions?.edgeStyle)?.name || 'Pill'}</span>
            </button>
            {expandedAccordions.has('edge') && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {EDGE_STYLES.map(edge => (
                    <button
                      key={edge.id}
                      onClick={() => updateCaptions({ edgeStyle: edge.id as any, borderRadius: edge.radius })}
                      className={`py-3 rounded-lg border-2 transition-all text-center ${
                        settings.captions?.edgeStyle === edge.id
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div 
                        className="w-12 h-6 mx-auto mb-1"
                        style={{ 
                          backgroundColor: '#34A49F',
                          borderRadius: edge.radius === 9999 ? '9999px' : `${edge.radius}px`,
                        }}
                      />
                      <span className="text-xs text-gray-600">{edge.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Background Color Accordion */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggleAccordion('background')}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                {expandedAccordions.has('background') ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <Palette className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Background Color</span>
              </div>
              <div className="flex items-center gap-2">
                <div 
                  className="w-5 h-5 rounded border border-gray-200"
                  style={{ backgroundColor: settings.captions?.backgroundColor || '#34A49F' }}
                />
                <span className="text-sm text-gray-500 font-mono">{settings.captions?.backgroundColor || '#34A49F'}</span>
              </div>
            </button>
            {expandedAccordions.has('background') && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="grid grid-cols-5 gap-2 mt-3 mb-3">
                  {PRESET_COLORS.map(color => (
                    <button
                      key={color.value}
                      onClick={() => updateCaptions({ backgroundColor: color.value })}
                      className={`aspect-square rounded-lg border-2 transition-all flex items-center justify-center ${
                        settings.captions?.backgroundColor === color.value
                          ? 'border-green-500 ring-2 ring-green-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ 
                        backgroundColor: color.value === 'transparent' ? '#fff' : color.value,
                        backgroundImage: color.value === 'transparent' 
                          ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
                          : 'none',
                        backgroundSize: '8px 8px',
                        backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
                      }}
                      title={color.name}
                    >
                      {settings.captions?.backgroundColor === color.value && (
                        <span className="text-white text-sm">✓</span>
                      )}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.captions?.backgroundColor === 'transparent' ? '#000000' : (settings.captions?.backgroundColor || '#34A49F')}
                    onChange={(e) => updateCaptions({ backgroundColor: e.target.value })}
                    className="w-8 h-8 rounded border border-gray-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.captions?.backgroundColor || '#34A49F'}
                    onChange={(e) => updateCaptions({ backgroundColor: e.target.value })}
                    className="flex-1 px-2 py-1 border border-gray-200 rounded text-sm font-mono"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Highlight Color Accordion */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggleAccordion('highlight')}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                {expandedAccordions.has('highlight') ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <span className="text-sm font-medium text-gray-700">Highlight Color</span>
              </div>
              <div className="flex items-center gap-2">
                <div 
                  className="w-5 h-5 rounded border border-gray-200"
                  style={{ backgroundColor: settings.captions?.highlightColor || '#FFCC00' }}
                />
                <span className="text-sm text-gray-500 font-mono">{settings.captions?.highlightColor || '#FFCC00'}</span>
              </div>
            </button>
            {expandedAccordions.has('highlight') && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <p className="text-xs text-gray-500 mt-3 mb-3">Active word color (TikTok-style)</p>
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {[
                    { name: 'Yellow', value: '#FFCC00' },
                    { name: 'Orange', value: '#FF9500' },
                    { name: 'Coral', value: '#FF6B6B' },
                    { name: 'Lime', value: '#A3E635' },
                    { name: 'Cyan', value: '#22D3EE' },
                    { name: 'Pink', value: '#F472B6' },
                    { name: 'Purple', value: '#A78BFA' },
                    { name: 'White', value: '#FFFFFF' },
                    { name: 'Gold', value: '#FFD700' },
                    { name: 'Mint', value: '#34D399' },
                  ].map(color => (
                    <button
                      key={color.value}
                      onClick={() => updateCaptions({ highlightColor: color.value })}
                      className={`aspect-square rounded-lg border-2 transition-all flex items-center justify-center ${
                        settings.captions?.highlightColor === color.value
                          ? 'border-green-500 ring-2 ring-green-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    >
                      {settings.captions?.highlightColor === color.value && (
                        <span className={`text-sm ${color.value === '#FFFFFF' ? 'text-gray-800' : 'text-white'}`}>✓</span>
                      )}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.captions?.highlightColor || '#FFCC00'}
                    onChange={(e) => updateCaptions({ highlightColor: e.target.value })}
                    className="w-8 h-8 rounded border border-gray-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.captions?.highlightColor || '#FFCC00'}
                    onChange={(e) => updateCaptions({ highlightColor: e.target.value })}
                    className="flex-1 px-2 py-1 border border-gray-200 rounded text-sm font-mono"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Position Accordion */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggleAccordion('position')}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                {expandedAccordions.has('position') ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <span className="text-sm font-medium text-gray-700">Position</span>
              </div>
              <span className="text-sm text-gray-500">{POSITION_LABELS.find(p => p.value === settings.captions?.position)?.label || 'Lower'}</span>
            </button>
            {expandedAccordions.has('position') && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="mt-3 flex gap-1">
                  {POSITION_LABELS.map(pos => (
                    <button
                      key={pos.value}
                      onClick={() => updateCaptions({ position: pos.value })}
                      className={`flex-1 py-2 rounded text-xs font-medium transition-all ${
                        settings.captions?.position === pos.value
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title={pos.label}
                    >
                      {pos.value}
                    </button>
                  ))}
                </div>
                <div className="mt-2 text-xs text-gray-400 text-center">
                  1 = Top Edge → 8 = Bottom Edge
                </div>
              </div>
            )}
          </div>

          {/* Advanced Accordion */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggleAccordion('advanced')}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                {expandedAccordions.has('advanced') ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <span className="text-sm font-medium text-gray-700">Advanced</span>
              </div>
              <span className="text-xs text-gray-400">Size, padding, text color</span>
            </button>
            {expandedAccordions.has('advanced') && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Font Size (px)</label>
                    <input
                      type="number"
                      value={settings.captions?.fontSize || 32}
                      onChange={(e) => updateCaptions({ fontSize: parseInt(e.target.value) || 32 })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      min={16}
                      max={72}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Padding (px)</label>
                    <input
                      type="number"
                      value={settings.captions?.padding || 16}
                      onChange={(e) => updateCaptions({ padding: parseInt(e.target.value) || 16 })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      min={0}
                      max={48}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 block mb-1">Text Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={settings.captions?.textColor || '#FFFFFF'}
                        onChange={(e) => updateCaptions({ textColor: e.target.value })}
                        className="w-8 h-8 rounded border border-gray-200 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings.captions?.textColor || '#FFFFFF'}
                        onChange={(e) => updateCaptions({ textColor: e.target.value })}
                        className="flex-1 px-2 py-1 border border-gray-200 rounded text-sm font-mono"
                      />
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 block mb-1">Width Mode</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => updateCaptions({ widthMode: 'snap' })}
                        className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          (settings.captions?.widthMode || 'snap') === 'snap'
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        Snap
                        <span className="block text-xs font-normal text-gray-400">Instant resize</span>
                      </button>
                      <button
                        onClick={() => updateCaptions({ widthMode: 'fixed' })}
                        className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          settings.captions?.widthMode === 'fixed'
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        Fixed
                        <span className="block text-xs font-normal text-gray-400">Constant width</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Animations Section */}
      {activeSection === 'animations' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <p className="text-sm text-gray-600">Click preview box to see animation. Toggle visibility with the eye icon.</p>
          </div>
          <div className="divide-y divide-gray-100">
            {ensureArray(settings.animations).map((anim, index) => {
              const effectInfo = EFFECTS.find(e => e.id === anim.id);
              return (
                <div
                  key={anim.id}
                  draggable
                  onDragStart={() => setDraggedIndex(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (draggedIndex !== null && draggedIndex !== index) {
                      moveAnimation(draggedIndex, index);
                    }
                    setDraggedIndex(null);
                  }}
                  className={`flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-grab ${
                    !anim.enabled ? 'opacity-50' : ''
                  }`}
                >
                  <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <AnimationPreview effectId={anim.id} />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-900 font-medium">{effectInfo?.name || anim.id}</span>
                    <p className="text-xs text-gray-400 truncate">{effectInfo?.description}</p>
                  </div>
                  <button
                    onClick={() => toggleAnimation(anim.id)}
                    className={`p-1.5 rounded transition-colors flex-shrink-0 ${
                      anim.enabled ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'
                    }`}
                  >
                    {anim.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sounds Section */}
      {activeSection === 'sounds' && (
        <div className="space-y-3">
          {/* Add new sound */}
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <h3 className="text-xs font-medium text-gray-500 mb-2">Add Custom Sound</h3>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newSoundName}
                  onChange={(e) => setNewSoundName(e.target.value)}
                  placeholder="Name (e.g., Whoosh)"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <button
                  onClick={addSound}
                  disabled={!newSoundName || !newSoundUrl}
                  className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm disabled:opacity-50 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <input
                type="text"
                value={newSoundUrl}
                onChange={(e) => setNewSoundUrl(e.target.value)}
                placeholder="URL (https://...)"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>
          </div>

          {/* Sound list */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <p className="text-sm text-gray-600">Click speaker icon to preview. Toggle visibility with eye icon.</p>
            </div>
            <div className="divide-y divide-gray-100">
              {ensureArray(settings.sounds).map(sound => (
                <div
                  key={sound.id}
                  className={`flex items-center gap-3 px-4 py-2.5 ${!sound.enabled ? 'opacity-50' : ''}`}
                >
                  <button
                    onClick={() => playSound(sound.url)}
                    className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center hover:bg-gray-200 transition-colors flex-shrink-0"
                    title="Click to preview"
                  >
                    <Volume2 className="w-4 h-4 text-gray-600" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900">{sound.name}</span>
                    <p className="text-xs text-gray-400">{sound.id === 'none' ? 'No sound effect' : 'Transition sound'}</p>
                  </div>
                  <button
                    onClick={() => toggleSound(sound.id)}
                    className={`p-1.5 rounded transition-colors flex-shrink-0 ${
                      sound.enabled ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'
                    }`}
                  >
                    {sound.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  {!['none', 'swoosh'].includes(sound.id) && (
                    <button
                      onClick={() => removeSound(sound.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* B-roll Sources Section */}
      {activeSection === 'sources' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <p className="text-sm text-gray-600">Where to get b-roll images for each scene.</p>
          </div>
          <div className="divide-y divide-gray-100">
            {ensureArray(settings.brollSources).map(source => {
              const sourceInfo = BROLL_SOURCES.find(b => b.id === source.id);
              return (
                <div
                  key={source.id}
                  className={`flex items-center gap-3 px-4 py-3 ${!source.enabled ? 'opacity-50' : ''}`}
                >
                  <span className="text-2xl flex-shrink-0">{sourceInfo?.icon}</span>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900">{sourceInfo?.name}</span>
                    <p className="text-xs text-gray-500">{sourceInfo?.description}</p>
                    <p className="text-xs text-gray-400 italic truncate">e.g. {sourceInfo?.example}</p>
                  </div>
                  <button
                    onClick={() => toggleSource(source.id)}
                    className={`p-1.5 rounded transition-colors flex-shrink-0 ${
                      source.enabled ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'
                    }`}
                  >
                    {source.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Components Section */}
      {activeSection === 'components' && (
        <div className="space-y-3">
          {/* Add new component */}
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <h3 className="text-xs font-medium text-gray-500 mb-2">Add Custom Component</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={newComponentIcon}
                onChange={(e) => setNewComponentIcon(e.target.value)}
                placeholder="🧩"
                className="w-12 px-2 py-2 border border-gray-200 rounded-lg text-sm text-center"
              />
              <input
                type="text"
                value={newComponentName}
                onChange={(e) => setNewComponentName(e.target.value)}
                placeholder="Component name"
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
              <button
                onClick={addComponent}
                disabled={!newComponentName}
                className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm disabled:opacity-50 hover:bg-green-700"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Component list */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <p className="text-sm text-gray-600">Animated React components for data visualization.</p>
            </div>
            <div className="divide-y divide-gray-100">
              {ensureArray(settings.components).map(comp => {
                // Mini visual preview for each component type
                const getComponentPreview = () => {
                  switch (comp.id) {
                    case 'metric-card':
                      return (
                        <div className="w-12 h-8 bg-gradient-to-br from-teal-500 to-teal-600 rounded flex items-center justify-center">
                          <span className="text-white text-xs font-bold">47%</span>
                        </div>
                      );
                    case 'donut-chart':
                      return (
                        <div className="w-8 h-8 rounded-full border-4 border-teal-500 border-t-gray-200 flex items-center justify-center">
                          <span className="text-[6px] font-bold text-gray-600">75</span>
                        </div>
                      );
                    case 'comparison-bars':
                      return (
                        <div className="w-12 h-8 flex flex-col justify-center gap-1">
                          <div className="h-1.5 bg-teal-500 rounded-full w-full" />
                          <div className="h-1.5 bg-gray-300 rounded-full w-3/4" />
                        </div>
                      );
                    case 'notification':
                      return (
                        <div className="w-12 h-7 bg-white border border-gray-200 rounded shadow-sm flex items-center px-1 gap-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full" />
                          <div className="flex-1 space-y-0.5">
                            <div className="h-1 bg-gray-300 rounded w-full" />
                            <div className="h-1 bg-gray-200 rounded w-2/3" />
                          </div>
                        </div>
                      );
                    case 'winner-card':
                      return (
                        <div className="w-12 h-8 bg-gradient-to-br from-yellow-400 to-amber-500 rounded flex items-center justify-center">
                          <span className="text-white text-xs">🏆</span>
                        </div>
                      );
                    case 'glass-card':
                      return (
                        <div className="w-12 h-8 bg-white/50 backdrop-blur border border-white/30 rounded flex items-center justify-center">
                          <span className="text-[6px] text-gray-500">Glass</span>
                        </div>
                      );
                    default:
                      return <span className="text-xl">{comp.icon}</span>;
                  }
                };
                
                const getComponentDescription = () => {
                  switch (comp.id) {
                    case 'metric-card': return 'Big number with label & trend';
                    case 'donut-chart': return 'Circular percentage chart';
                    case 'comparison-bars': return 'Side-by-side bar comparison';
                    case 'notification': return 'Toast-style alert popup';
                    case 'winner-card': return 'Highlight winning option';
                    case 'glass-card': return 'Frosted glass effect card';
                    default: return 'Custom component';
                  }
                };
                
                return (
                  <div
                    key={comp.id}
                    className={`flex items-center gap-3 px-4 py-3 ${!comp.enabled ? 'opacity-50' : ''}`}
                  >
                    {getComponentPreview()}
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900">{comp.name}</span>
                      <p className="text-xs text-gray-400 truncate">{getComponentDescription()}</p>
                    </div>
                    <button
                      onClick={() => toggleComponent(comp.id)}
                      className={`p-1.5 rounded transition-colors flex-shrink-0 ${
                        comp.enabled ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'
                      }`}
                    >
                      {comp.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => removeComponent(comp.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Last saved */}
      {settings.updatedAt && (
        <p className="text-xs text-gray-400 mt-4 text-center">
          Last saved: {new Date(settings.updatedAt).toLocaleString()}
        </p>
      )}
    </div>
  );
}

// Export for use in other components
export { loadSettings, DEFAULT_SETTINGS, DEFAULT_CAPTIONS, GOOGLE_FONTS, POSITION_LABELS, FONT_WEIGHTS, TEXT_CASES, EDGE_STYLES };
export type { AppSettings, CaptionSettings, CaptionPreset };
